/**
 * useReadingEvents — subscribes to NowPlayingContext state and emits passive
 * reading events into the module-level event buffer.
 *
 * Spec: docs/features/assessment-data-layer.md
 *
 * Hard contract: this hook MUST NOT mutate NowPlayingContext or any of the
 * playback files (usePlayback, useWordTracking, useManifest). It only reads.
 * That contract is what protects this code from colliding with the parallel
 * accessibility work in chore/accessibility-phase-d.
 */

import { useEffect, useRef } from 'react';
import { useNowPlaying } from '../contexts/NowPlayingContext';
import { useAuth } from '../contexts/AuthContext';
import { eventBuffer } from '../services/event-buffer';
import { HEARTBEAT_INTERVAL_MS, PAUSE_TIMEOUT_MS } from '../config/events';

function newId() {
  // Browsers since 2022 ship crypto.randomUUID(); fall back if not available.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Best-effort fallback (not RFC 4122 strict, but UUID-shaped enough to pass
  // the server's UUID regex and remain idempotent).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function useReadingEvents() {
  const {
    bookId,
    audioRef,
    currentChunkIndex,
    currentChapterIndex,
    currentWordIndex,
    playbackSpeed,
    isPlaying,
    manifest,
  } = useNowPlaying();
  const { token, user } = useAuth();
  const accessCode = user?.accessCode ?? null;

  const sessionIdRef = useRef(null);
  const sessionStartedRef = useRef(false);
  const lastHeartbeatTimeRef = useRef(0);
  const lastChapterRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const lastBookIdRef = useRef(null);

  // Keep the buffer's auth/session in sync.
  useEffect(() => {
    if (token && sessionIdRef.current) {
      eventBuffer.setSession({ token, sessionId: sessionIdRef.current });
    } else if (!token) {
      eventBuffer.clearSession();
    }
  }, [token]);

  // Session start when bookId changes (new book = new session).
  useEffect(() => {
    if (!accessCode || !token) return;

    // No book → end any prior session.
    if (!bookId) {
      if (lastBookIdRef.current && sessionStartedRef.current) {
        emitEvent({
          event_type: 'session_end',
          book_id: lastBookIdRef.current,
          payload: { reason: 'book_cleared', ms_since_prev_heartbeat: 0 },
        });
        sessionStartedRef.current = false;
      }
      lastBookIdRef.current = null;
      return;
    }

    if (lastBookIdRef.current === bookId) return;

    // End previous session if there was one.
    if (lastBookIdRef.current && sessionStartedRef.current) {
      emitEvent({
        event_type: 'session_end',
        book_id: lastBookIdRef.current,
        payload: { reason: 'book_changed', ms_since_prev_heartbeat: 0 },
      });
    }

    // Start new session.
    sessionIdRef.current = newId();
    sessionStartedRef.current = true;
    lastHeartbeatTimeRef.current = Date.now();
    lastChapterRef.current = null;
    eventBuffer.setSession({ token, sessionId: sessionIdRef.current });

    emitEvent({
      event_type: 'session_start',
      book_id: bookId,
      payload: {},
    });

    lastBookIdRef.current = bookId;
    // emitEvent intentionally not in deps — defined inline below using the
    // accessCode/sessionIdRef that are already accounted for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, accessCode, token]);

  // Heartbeat: timeupdate-driven. Throttling in hidden tabs produces fewer
  // heartbeats, never wrong totals.
  useEffect(() => {
    if (!bookId || !audioRef?.current || !accessCode) return;
    const audio = audioRef.current;

    const onTimeUpdate = () => {
      if (audio.paused) return;
      const now = Date.now();
      const elapsed = now - lastHeartbeatTimeRef.current;
      if (elapsed < HEARTBEAT_INTERVAL_MS) return;

      emitEvent({
        event_type: 'chunk_progress',
        book_id: bookId,
        payload: {
          word_index: currentWordIndex ?? 0,
          audio_global_time: globalAudioTime(audio, manifest, currentChunkIndex),
          playback_rate: parseFloat(playbackSpeed) || 1,
          chapter_idx: currentChapterIndex ?? 0,
          ms_since_prev_heartbeat: elapsed,
        },
      });
      lastHeartbeatTimeRef.current = now;
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => audio.removeEventListener('timeupdate', onTimeUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, audioRef, currentWordIndex, currentChunkIndex, currentChapterIndex, playbackSpeed, accessCode, manifest]);

  // Chapter change.
  useEffect(() => {
    if (!bookId || !accessCode || currentChapterIndex == null) return;
    if (lastChapterRef.current !== null && lastChapterRef.current !== currentChapterIndex) {
      emitEvent({
        event_type: 'chapter_change',
        book_id: bookId,
        payload: {
          from_chapter: lastChapterRef.current,
          to_chapter: currentChapterIndex,
        },
      });
    }
    lastChapterRef.current = currentChapterIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, currentChapterIndex, accessCode]);

  // Pause timeout: 5min of pause = session_end.
  useEffect(() => {
    if (!bookId || !accessCode) return;
    if (isPlaying) {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
      return;
    }
    pauseTimeoutRef.current = setTimeout(() => {
      if (sessionStartedRef.current) {
        emitEvent({
          event_type: 'session_end',
          book_id: bookId,
          payload: { reason: 'pause_timeout', ms_since_prev_heartbeat: 0 },
        });
        sessionStartedRef.current = false;
      }
    }, PAUSE_TIMEOUT_MS);

    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, accessCode, isPlaying]);

  // book_complete: fires when audio ends on the final chunk.
  useEffect(() => {
    if (!bookId || !manifest || !audioRef?.current || !accessCode) return;
    const audio = audioRef.current;
    const totalChunks = manifest.chunks?.length ?? 0;
    if (totalChunks === 0) return;

    const onEnded = () => {
      if (currentChunkIndex === totalChunks - 1) {
        emitEvent({
          event_type: 'book_complete',
          book_id: bookId,
          payload: {},
        });
      }
    };
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, manifest, audioRef, currentChunkIndex, accessCode]);

  function emitEvent({ event_type, book_id, payload }) {
    if (!accessCode) return;
    eventBuffer.enqueue(
      {
        client_event_id: newId(),
        event_type,
        book_id,
        client_timestamp: new Date().toISOString(),
        payload: payload ?? {},
      },
      accessCode,
    );
  }
}

/**
 * Compute audio's position on the global timeline (chunk-local time + sum of
 * preceding chunk durations). Manifest v2 carries durations per chunk.
 */
function globalAudioTime(audio, manifest, currentChunkIndex) {
  const local = audio?.currentTime ?? 0;
  const chunks = manifest?.chunks ?? [];
  let prefix = 0;
  for (let i = 0; i < currentChunkIndex && i < chunks.length; i++) {
    prefix += chunks[i]?.duration ?? 0;
  }
  return prefix + local;
}
